import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  LinearProgress,
  Alert,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
  WorkOutline as JobIcon,
  AssignmentOutlined as AssessmentIcon,
  CodeOutlined as TechnicalIcon,
  PlayArrowOutlined as StartIcon,
  CheckCircleOutlined as CompletedIcon,
  TimerOutlined as TimerIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import candidateTheme from '../../theme/candidateTheme';
import CandidateHeader from '../../components/CandidateHeader';
import api from '../../api';
import { showSuccess, showError, showInfo } from '../../utils/sweetAlertConfig';

/**
 * Page to display candidate's job applications and available assessments
 */
const CandidateApplications = () => {
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCandidateData();
    loadApplications();
  }, []);

  const loadCandidateData = () => {
    const stored = localStorage.getItem('candidate_data');
    if (stored) {
      setCandidate(JSON.parse(stored));
    }
  };

  const loadApplications = async () => {
    try {
      const response = await api.get('/candidate/applications');
      setApplications(response.data.applications || []);
    } catch (error) {
      console.error('Error loading applications:', error);
      showError('Error', 'Failed to load your applications');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAssessment = async (assessmentId, assessmentType) => {
    try {
      if (assessmentType === 'digital') {
        // Navigate to digital assessment
        navigate(`/candidate/assessment/${assessmentId}`);
      } else if (assessmentType === 'technical') {
        // Navigate to technical assessment
        navigate(`/candidate/technical-assessment/${assessmentId}`);
      }
    } catch (error) {
      showError('Error', 'Failed to start assessment');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available': return 'primary';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'available': return <StartIcon />;
      case 'in_progress': return <TimerIcon />;
      case 'completed': return <CompletedIcon />;
      case 'expired': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  if (loading) {
    return (
      <ThemeProvider theme={candidateTheme}>
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)' }}>
          <CandidateHeader candidate={candidate} />
          <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography variant="h6">Loading your applications...</Typography>
            </Paper>
          </Container>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={candidateTheme}>
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%)' }}>
        <CandidateHeader candidate={candidate} />

        <Container maxWidth="md" sx={{ py: 4 }}>
          {/* Welcome Section */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 600, 
                color: '#29204C',
                mb: 1,
                fontSize: { xs: '1.5rem', sm: '2rem' }
              }}
            >
              My Applications & Assessments
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ color: 'text.secondary' }}
            >
              View your job applications and take available assessments
            </Typography>
          </Box>

          {applications.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <JobIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Applications Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You haven't applied for any positions yet.
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {applications.map((application) => (
                <Grid item xs={12} key={application.application_id}>
                  <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
                    <CardContent sx={{ p: 3 }}>
                      {/* Application Header */}
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <JobIcon sx={{ mr: 2, color: '#29204C', fontSize: '2rem' }} />
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h5" sx={{ fontWeight: 600, color: '#29204C' }}>
                            {application.job?.posting_title || 'Job Position'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Application ID: {application.application_id}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Applied on: {new Date(application.application_date).toLocaleDateString()}
                          </Typography>
                        </Box>
                        <Chip 
                          label={application.status} 
                          color={application.status === 'accepted' ? 'success' : application.status === 'pending' ? 'warning' : 'default'}
                          sx={{ fontWeight: 600 }}
                        />
                      </Box>

                      <Divider sx={{ my: 2 }} />

                      {/* Assessments Section */}
                      <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                        <AssessmentIcon sx={{ mr: 1 }} />
                        Available Assessments
                      </Typography>

                      {application.assessments && application.assessments.length > 0 ? (
                        <Grid container spacing={2}>
                          {application.assessments.map((assessment) => (
                            <Grid item xs={12} sm={6} key={assessment.id}>
                              <Paper 
                                sx={{ 
                                  p: 2, 
                                  border: '1px solid #e0e0e0',
                                  borderRadius: 2,
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    boxShadow: 3,
                                    transform: 'translateY(-2px)'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  {assessment.type === 'digital' ? (
                                    <AssessmentIcon sx={{ mr: 1, color: '#29204C' }} />
                                  ) : (
                                    <TechnicalIcon sx={{ mr: 1, color: '#68B977' }} />
                                  )}
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {assessment.type === 'digital' ? 'Digital Assessment' : 'Technical Task'}
                                  </Typography>
                                </Box>

                                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                                  {assessment.title}
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                  {assessment.type === 'digital' 
                                    ? 'Multiple choice and essay questions'
                                    : assessment.description || 'Practical task or coding challenge'
                                  }
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Chip
                                    icon={getStatusIcon(assessment.status)}
                                    label={(assessment.status || 'pending').replace('_', ' ').toUpperCase()}
                                    color={getStatusColor(assessment.status)}
                                    size="small"
                                    sx={{ fontWeight: 500 }}
                                  />

                                  {assessment.status === 'available' && (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      startIcon={<StartIcon />}
                                      onClick={() => handleStartAssessment(
                                        assessment.assessment_id,
                                        assessment.type
                                      )}
                                      sx={{ 
                                        backgroundColor: assessment.type === 'digital' ? '#29204C' : '#68B977',
                                        '&:hover': {
                                          backgroundColor: assessment.type === 'digital' ? '#1a1530' : '#4a8455'
                                        }
                                      }}
                                    >
                                      Start
                                    </Button>
                                  )}

                                  {assessment.status === 'completed' && assessment.score && (
                                    <Tooltip title={`Score: ${assessment.score}%`}>
                                      <Chip 
                                        label={`${assessment.score}%`}
                                        color="success"
                                        size="small"
                                        sx={{ fontWeight: 600 }}
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </Paper>
                            </Grid>
                          ))}
                        </Grid>
                      ) : (
                        <Alert severity="info" sx={{ mt: 2 }}>
                          <Typography variant="body2">
                            No assessments are currently available for this position.
                          </Typography>
                        </Alert>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Navigation */}
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/candidate/dashboard')}
              sx={{ mr: 2 }}
            >
              Back to Profile
            </Button>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default CandidateApplications;